import { PersonalInfoData } from "@faire/deprecated-retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { useIsBrandPreview } from "@faire/deprecated-retailer-visitor-shared/lib/getIsBrandPreview";
import { useSettingShopToBuyLoggedOut } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingShopToBuyLoggedOut";
import {
  CountryField,
  EmailOptOutField,
  LangField,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/fields/localeFields";
import {
  FirstNameField,
  LastNameField,
  PasswordField,
  useEmailAddressSchema,
  useFirstNameSchema,
  useLastNameSchema,
  usePasswordSchema,
  EmailAddressField,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/fields/personalFields";
import { useRetailerSubUser } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/retailerSubUserHooks";
import { useSignUpEventTracker } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import {
  FaireDirectDisclaimer,
  FaireDirectLoginDisclaimer,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/personal/__internal__/faire-direct/FaireDirectDisclaimers";
import { FaireDirectEmailAddressField } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/personal/__internal__/faire-direct/FaireDirectEmailAddressField";
import { PrivacyPolicyNote } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/personal/__internal__/sub-user/PrivacyPolicyNote";
import { useServerValidatePersonalInfo } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/personal/__internal__/useServerValidatePersonalInfo";
import { Step } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/Step";
import { Core } from "@faire/design-tokens";
import { trackSignupSignUpFormStep1BasicInfoView } from "@faire/web-api--source/events/signup/view/signUpFormStep1BasicInfo";
import { ILocaleKey } from "@faire/web-api--source/indigofair/locale/ILocaleKey";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { getWebApiErrorMessage } from "@faire/web/common/getErrorMessage";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Column, Flex } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { Collapsable } from "@faire/web/ui/Collapsable/Collapsable";
import { useDelayedCallback } from "@faire/web/ui/hooks/useDelayedCallback";
import { useState } from "@faire/web/ui/hooks/useState";
import { Form, setNestedObjectValues, useFormikContext } from "formik";
import React, { FormEvent, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import * as Yup from "yup";

export interface IElementsVisibility {
  name: boolean;
  password: boolean;
  email: boolean;
  emailOptOut: boolean;
  privacyPolicyNote: boolean;
  country: boolean;
  language: boolean;
  faireDirectRequiredEmailAddressSignUpElements: boolean;
  smsOptIn: boolean;
}

interface IProps {
  visibleElements: IElementsVisibility;
  isLoading: boolean;
  onLanguageChange: (
    oldLocaleKey: ILocaleKey | undefined,
    newLocaleKey: ILocaleKey | undefined,
    values: PersonalInfoData
  ) => void;
}

export const PersonalInfoForm: React.FC<IProps> = ({
  isLoading,
  visibleElements,
  onLanguageChange,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    dirty,
    handleSubmit,
    isValid,
    setTouched,
    values,
    setFieldError,
    validateForm,
    initialValues,
  } = useFormikContext<PersonalInfoData>();
  const isBrandPreview = useIsBrandPreview();
  const { serverValidatePersonalInfo } = useServerValidatePersonalInfo();
  const { trackFieldValue } = useSignUpEventTracker(Step.PERSONAL_INFO);
  const { isRetailerSubUser } = useRetailerSubUser();
  const withInitialValues = hasInitialValues(initialValues);
  const [error, setError] = useState<string | undefined>(undefined);
  const ctaText = useCTAText();

  const isCountryUSA =
    values.country === THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;

  useEffect(() => {
    setTouched({
      first_name: !!initialValues.first_name,
      last_name: !!initialValues.last_name,
      password: !!initialValues.password,
    });
  }, [initialValues, setTouched]);

  useEffect(() => {
    onLanguageChange(initialValues.locale_key, values.locale_key, values);
  }, [values, initialValues.locale_key, onLanguageChange]);

  useEffect(() => {
    if (!isRetailerSubUser) {
      trackSignupSignUpFormStep1BasicInfoView();
    }
  }, [isRetailerSubUser]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    const validationErrors = await validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setTouched(setNestedObjectValues(validationErrors, true));
      setIsSubmitting(false);
      return;
    }

    try {
      const isValidPersonalInfo = await serverValidatePersonalInfo(
        values,
        setFieldError
      );
      if (isValidPersonalInfo) {
        handleSubmit();
      }
    } catch (error) {
      setError(getWebApiErrorMessage(error, strictLocalize));
    }
    setIsSubmitting(false);
  };

  return (
    <FullWidthForm onSubmit={onSubmit}>
      <Column gap="4x">
        {visibleElements.name ? (
          <FirstAndLastNameFields trackChange={trackFieldValue} />
        ) : null}
        {visibleElements.faireDirectRequiredEmailAddressSignUpElements ? (
          <FaireDirectEmailAddressField trackChange={trackFieldValue} />
        ) : null}
        {visibleElements.email ? (
          <EmailAddressField trackChange={trackFieldValue} disabled />
        ) : null}
        {visibleElements.password ? (
          <PasswordField trackChange={trackFieldValue} />
        ) : null}
        {visibleElements.country ? (
          <CountryField trackChange={trackFieldValue} />
        ) : null}
        {visibleElements.language ? (
          <LangField
            country={values.country ?? THREE_LETTER_PRIMARY_COUNTRY_CODES.USA}
            trackChange={trackFieldValue}
          />
        ) : null}
      </Column>
      {visibleElements.emailOptOut ? (
        <Collapsable isCollapsed={isCountryUSA}>
          <Spacer height="2x" />
          <EmailOptOutField disabled={isBrandPreview} />
        </Collapsable>
      ) : null}
      <Spacer
        height={{
          mobileAndAbove: "4x",
          desktopAndAbove: "5x",
        }}
      />
      <Button
        type="submit"
        data-test-id="next"
        disabled={!isValid || (!dirty && !withInitialValues)}
        loading={isLoading || isSubmitting}
      >
        {ctaText}
      </Button>
      {visibleElements.privacyPolicyNote ? (
        <>
          <Spacer height="3x" />
          <PrivacyPolicyNote />
        </>
      ) : null}
      {visibleElements.faireDirectRequiredEmailAddressSignUpElements ? (
        <>
          <Spacer height="3x" />
          <FaireDirectLoginDisclaimer />
          <Spacer height="4x" />
          <FaireDirectDisclaimer />
        </>
      ) : null}
      {error ? (
        <>
          <Spacer height="3x" />
          <Typography role="alert" color={Core.text.critical} align="center">
            {error}
          </Typography>
        </>
      ) : null}
    </FullWidthForm>
  );
};

export const useSchema = ({
  requirePassword,
  requireEmail,
}: {
  requirePassword: boolean;
  requireEmail: boolean;
}) => {
  const { last_name } = useLastNameSchema();
  const { first_name } = useFirstNameSchema();
  const { password } = usePasswordSchema();
  const { email_address } = useEmailAddressSchema();

  return useMemo(
    () =>
      Yup.object().shape({
        first_name: first_name(),
        last_name: last_name(),
        password: requirePassword ? password() : Yup.string().notRequired(),
        email_address: requireEmail
          ? email_address()
          : Yup.string().notRequired(),
      }),
    [
      first_name,
      last_name,
      password,
      requirePassword,
      requireEmail,
      email_address,
    ]
  );
};

const FirstAndLastNameFields: React.FC<{
  trackChange: (name: string, value: string) => void;
}> = ({ trackChange }) => {
  const { dirty, initialValues } = useFormikContext<PersonalInfoData>();
  const withInitialValues = hasInitialValues(initialValues);
  const autoFocus = !withInitialValues && !dirty;
  const ref = useRef<HTMLInputElement>(null);
  const focusInputField = useDelayedCallback(() => {
    ref.current?.focus();
  });
  useEffect(() => {
    if (autoFocus) {
      focusInputField(300);
    }
  }, [autoFocus, focusInputField]);

  return (
    <Flex direction={{ mobileAndAbove: "column", desktopAndAbove: "row" }}>
      <FirstNameField ref={ref} trackChange={trackChange} />
      <Spacer
        size={{
          mobileAndAbove: "4x",
          desktopAndAbove: "2x",
        }}
      />
      <LastNameField trackChange={trackChange} />
    </Flex>
  );
};

const useCTAText = () => {
  const { strictLocalize } = useStrictLocalization();
  const { isRetailerSubUser } = useRetailerSubUser();
  const isShopToBuyLoggedOut = useSettingShopToBuyLoggedOut();

  if (isRetailerSubUser) {
    if (isShopToBuyLoggedOut) {
      return strictLocalize({
        id: "nMuNvD",
        defaultMessage: "Start buying",
        description: {
          text: "Button to submit form data and create the login.",
          img: "https://cdn.faire.com/fastly/12f12b6409768961452314694ac853c531ddd0ee3b56cbf49298679f830c1d92.png",
        },
      });
    }
    return strictLocalize({
      id: "7ZwAHn",
      defaultMessage: "Start shopping",
      description: {
        text: "Button to submit form data and create the login.",
        img: "https://cdn.faire.com/fastly/b834a9e409f7b9508529765ec3bf098965d5720962b87084b4928f4d9febcb3f.png",
      },
    });
  }
  return strictLocalize({
    id: "m853T8",
    defaultMessage: "Next",
    description: { text: "Button to visit the next page" },
  });
};

const hasInitialValues = (initialValues: PersonalInfoData) => {
  return !!(
    initialValues.first_name ||
    initialValues.last_name ||
    initialValues.password
  );
};

const FullWidthForm = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
